import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import { formatCNPJ, maskCNPJ } from '../utils/formatters';

interface ExportRow {
  id: number;
  name: string;
  cnpj: string | null;
  status: number;
  created_at: string;
  opportunities: number;
  highlights: number;
  confidentials: number;
  total_credits: number;
}

interface ExportFilters {
  scope: string;
  creditsFilter: string;
  search: string;
  searchType: string;
}

interface CompanyCreditsExportModalProps {
  initialFilters: ExportFilters;
  onClose: () => void;
}

const ORDER_OPTIONS = [
  { value: 'total_credits', label: 'Total de créditos' },
  { value: 'opportunities', label: 'Vagas' },
  { value: 'highlights', label: 'Destaques' },
  { value: 'confidentials', label: 'Confidenciais' },
  { value: 'name', label: 'Nome da empresa' },
  { value: 'id', label: 'ID' },
  { value: 'created_at', label: 'Data de criação' },
];

const CompanyCreditsExportModal: React.FC<CompanyCreditsExportModalProps> = ({ initialFilters, onClose }) => {
  const [scope, setScope] = useState(initialFilters.scope);
  const [creditsFilter, setCreditsFilter] = useState(initialFilters.creditsFilter);
  const [searchType, setSearchType] = useState(initialFilters.searchType);
  const [search, setSearch] = useState(initialFilters.search);
  const [orderBy, setOrderBy] = useState('total_credits');
  const [orderDir, setOrderDir] = useState('desc');
  const [rows, setRows] = useState<string>('');

  const [total, setTotal] = useState<number | null>(null);
  const [maxRows, setMaxRows] = useState(100000);
  const [countLoading, setCountLoading] = useState(false);
  const [preview, setPreview] = useState<ExportRow[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (scope !== 'all') params.set('status', scope === 'active' ? '1' : '0');
    if (creditsFilter !== 'all') params.set('hasCredits', creditsFilter === 'with' ? '1' : '0');
    if (search) {
      params.set('search', search);
      params.set('searchType', searchType);
    }
    params.set('orderBy', orderBy);
    params.set('orderDir', orderDir);
    return params;
  }, [scope, creditsFilter, search, searchType, orderBy, orderDir]);

  // Toda mudança de filtro/ordem invalida o preview: o download volta a ficar travado
  // até revisualizar, pra ninguém baixar uma planilha diferente do que viu.
  useEffect(() => {
    setPreview(null);
  }, [scope, creditsFilter, search, searchType, orderBy, orderDir]);

  useEffect(() => {
    let canceled = false;

    const fetchCount = async () => {
      try {
        setCountLoading(true);
        const params = buildParams();
        params.set('rows', '0');
        const response = await api.get(`/company-credits/export/preview?${params}`);
        if (canceled) return;
        setTotal(response.data.total);
        setMaxRows(response.data.maxRows);
      } catch (error: any) {
        if (!canceled) toast.error('Erro ao contar empresas do filtro');
      } finally {
        if (!canceled) setCountLoading(false);
      }
    };

    fetchCount();
    return () => { canceled = true; };
  }, [buildParams]);

  const exportLimit = Math.min(total ?? 0, maxRows);
  const requestedRows = rows.trim() === '' ? exportLimit : Math.min(Number(rows) || 0, exportLimit);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(searchType === 'cnpj' ? maskCNPJ(e.target.value) : e.target.value);
  };

  const handleSearchTypeChange = (newType: string) => {
    setSearchType(newType);
    setSearch('');
  };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      const response = await api.get(`/company-credits/export/preview?${buildParams()}`);
      setPreview(response.data.rows);
      setTotal(response.data.total);
      if (!response.data.rows.length) {
        toast.info('Nenhuma empresa encontrada para esses filtros');
      }
    } catch (error: any) {
      toast.error('Erro ao gerar pré-visualização');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const params = buildParams();
      params.set('rows', String(requestedRows));

      const response = await api.get(`/company-credits/export?${params}`, { responseType: 'blob' });

      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : 'creditos-empresas.xlsx';

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Planilha gerada com ${requestedRows} linha(s)`);
      onClose();
    } catch (error: any) {
      toast.error('Erro ao exportar créditos');
    } finally {
      setDownloading(false);
    }
  };

  const canDownload = !!preview && preview.length > 0 && requestedRows > 0 && !downloading;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Exportar Créditos</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        <div style={styles.content}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Filtros</h3>
            <div style={styles.grid}>
              <div>
                <label style={styles.label}>Status:</label>
                <select value={scope} onChange={(e) => setScope(e.target.value)} style={styles.select}>
                  <option value="all">Todos</option>
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Créditos:</label>
                <select value={creditsFilter} onChange={(e) => setCreditsFilter(e.target.value)} style={styles.select}>
                  <option value="all">Todos</option>
                  <option value="with">Com créditos</option>
                  <option value="without">Sem créditos</option>
                </select>
              </div>
            </div>

            <div style={styles.grid}>
              <div>
                <label style={styles.label}>Buscar por:</label>
                <select value={searchType} onChange={(e) => handleSearchTypeChange(e.target.value)} style={styles.select}>
                  <option value="name">Nome</option>
                  <option value="cnpj">CNPJ</option>
                  <option value="id">ID</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Termo:</label>
                <input
                  type="text"
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="deixe vazio para não filtrar"
                  style={styles.input}
                  maxLength={searchType === 'cnpj' ? 18 : undefined}
                />
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Ordenação e volume</h3>
            <div style={styles.grid}>
              <div>
                <label style={styles.label}>Ordenar por:</label>
                <select value={orderBy} onChange={(e) => setOrderBy(e.target.value)} style={styles.select}>
                  {ORDER_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Direção:</label>
                <select value={orderDir} onChange={(e) => setOrderDir(e.target.value)} style={styles.select}>
                  <option value="desc">Maior para menor</option>
                  <option value="asc">Menor para maior</option>
                </select>
              </div>
            </div>

            <div style={styles.rowsRow}>
              <div style={styles.rowsInputWrapper}>
                <label style={styles.label}>Quantidade de linhas:</label>
                <input
                  type="number"
                  min={1}
                  max={exportLimit || undefined}
                  value={rows}
                  onChange={(e) => setRows(e.target.value)}
                  placeholder={exportLimit ? String(exportLimit) : '0'}
                  style={styles.input}
                />
              </div>
              <div style={styles.countBox}>
                {countLoading ? (
                  <span style={styles.countMuted}>contando...</span>
                ) : (
                  <>
                    <div style={styles.countValue}>{total ?? 0}</div>
                    <div style={styles.countMuted}>
                      empresa(s) neste filtro
                      {total !== null && total > maxRows && (
                        <> — teto de {maxRows} por export</>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            <p style={styles.hint}>
              Vazio exporta o máximo disponível ({exportLimit}). Alterar filtros ou ordenação exige nova pré-visualização.
            </p>
          </div>

          <div style={styles.section}>
            <div style={styles.previewHeader}>
              <h3 style={styles.sectionTitle}>Pré-visualização</h3>
              <button
                onClick={handlePreview}
                disabled={previewLoading}
                style={{
                  ...styles.previewBtn,
                  ...(previewLoading ? { backgroundColor: colors.textLight, cursor: 'not-allowed' } : {}),
                }}
              >
                {previewLoading ? 'Carregando...' : 'Visualizar preview'}
              </button>
            </div>

            {preview === null ? (
              <div style={styles.previewEmpty}>
                Clique em "Visualizar preview" para conferir as primeiras linhas antes de baixar.
              </div>
            ) : preview.length === 0 ? (
              <div style={styles.previewEmpty}>Nenhuma empresa encontrada para esses filtros.</div>
            ) : (
              <>
                <div style={styles.previewNote}>
                  Mostrando as {preview.length} primeiras de {requestedRows} linha(s) que serão exportadas.
                </div>
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.tableHeader}>
                        <th style={styles.th}>ID</th>
                        <th style={styles.th}>Empresa</th>
                        <th style={styles.th}>CNPJ</th>
                        <th style={styles.thCenter}>Vagas</th>
                        <th style={styles.thCenter}>Dest.</th>
                        <th style={styles.thCenter}>Conf.</th>
                        <th style={styles.thCenter}>Total</th>
                        <th style={styles.thCenter}>Ativo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map(row => (
                        <tr key={row.id} style={styles.tableRow}>
                          <td style={styles.td}>{row.id}</td>
                          <td style={styles.tdName} title={row.name}>{row.name}</td>
                          <td style={styles.td}>{formatCNPJ(row.cnpj)}</td>
                          <td style={styles.tdCenter}>{row.opportunities}</td>
                          <td style={styles.tdCenter}>{row.highlights}</td>
                          <td style={styles.tdCenter}>{row.confidentials}</td>
                          <td style={styles.tdTotal}>{row.total_credits}</td>
                          <td style={styles.tdCenter}>{row.status === 1 ? 'SIM' : 'NÃO'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelBtn}>Cancelar</button>
          <button
            onClick={handleDownload}
            disabled={!canDownload}
            style={{
              ...styles.downloadBtn,
              ...(canDownload ? {} : { backgroundColor: colors.textLight, cursor: 'not-allowed' }),
            }}
            title={preview ? '' : 'Visualize o preview para liberar o download'}
          >
            {downloading ? 'Gerando...' : 'Baixar Excel'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '92%',
    maxWidth: '900px',
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
    lineHeight: 1,
  },
  content: {
    padding: '1.5rem',
    overflowY: 'auto',
  },
  section: {
    marginBottom: '1.75rem',
  },
  sectionTitle: {
    margin: '0 0 0.75rem 0',
    color: colors.text,
    fontSize: '1.1rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.4rem',
    fontWeight: 'bold',
    color: colors.text,
    fontSize: '0.9rem',
  },
  select: {
    width: '100%',
    padding: '0.6rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    backgroundColor: colors.white,
  },
  input: {
    width: '100%',
    padding: '0.6rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  rowsRow: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  rowsInputWrapper: {
    flex: '1 1 220px',
  },
  countBox: {
    flex: '1 1 220px',
    padding: '0.6rem 1rem',
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
  },
  countValue: {
    fontSize: '1.4rem',
    fontWeight: 'bold',
    color: colors.primary,
    lineHeight: 1.2,
  },
  countMuted: {
    fontSize: '0.8rem',
    color: colors.textLight,
  },
  hint: {
    margin: '0.75rem 0 0 0',
    fontSize: '0.8rem',
    color: colors.textLight,
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  previewBtn: {
    padding: '0.6rem 1.25rem',
    backgroundColor: colors.accent,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
    fontWeight: 'bold',
  },
  previewEmpty: {
    padding: '1.5rem',
    textAlign: 'center',
    color: colors.textLight,
    backgroundColor: colors.background,
    border: `1px dashed ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  previewNote: {
    fontSize: '0.85rem',
    color: colors.textLight,
    marginBottom: '0.5rem',
  },
  tableWrapper: {
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: '320px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.85rem',
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '0.6rem',
    textAlign: 'left',
    fontWeight: 600,
    position: 'sticky',
    top: 0,
    backgroundColor: colors.primary,
  },
  thCenter: {
    padding: '0.6rem',
    textAlign: 'center',
    fontWeight: 600,
    position: 'sticky',
    top: 0,
    backgroundColor: colors.primary,
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '0.5rem 0.6rem',
    color: colors.text,
    whiteSpace: 'nowrap',
  },
  tdName: {
    padding: '0.5rem 0.6rem',
    color: colors.text,
    maxWidth: '260px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tdCenter: {
    padding: '0.5rem 0.6rem',
    color: colors.text,
    textAlign: 'center',
  },
  tdTotal: {
    padding: '0.5rem 0.6rem',
    color: colors.primary,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: `1px solid ${colors.border}`,
  },
  cancelBtn: {
    padding: '0.7rem 1.5rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
  },
  downloadBtn: {
    padding: '0.7rem 1.75rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
    fontWeight: 'bold',
  },
};

export default CompanyCreditsExportModal;
